import React, {useState, useRef} from 'react';
import {Box, Stack, Typography} from '@mui/material';
import {t} from 'i18next';
import {v4 as uuidv4} from 'uuid';
import {Query} from '../../../types/Query';
import {getOperatorLabel} from '../operatorMenuUtils';
import {Criteria, CriteriaType} from '../../../types/Criteria';
import {Field} from '../../../types/Field';
import {RoleId} from '../../../../../shared/constants';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrOperatorButton from '../operator-button/OperatorButton';
import ChipPanel from '../query-panel/chip-panel/ChipPanel';
import FieldTypeDrawer from '../field-type-drawer/FieldTypeDrawer';
import ProductionsFieldType from '../field-types/productions-field-type/ProductionsFieldType';
import SecurityGroupType from '../field-types/security-group-field-type/SecurityGroupType';
import ScoreFieldType from '../field-types/score-field-type/ScoreFieldType';
import AssignmentFieldType from '../field-types/assignments-field-type/AssignmentFieldType';
import TagsFieldType from '../field-types/tags-field-type/TagsFieldType';
import FolderFieldType from '../field-types/folder-field-type/FolderFieldType';
import RedactionLabelsField from '../field-types/redaction-labels-field/RedactionLabelsField';
import SavedSearchResultsFieldType from '../field-types/saved-search-results-field/SavedSearchResultsField';

class TemporaryCriteria implements Criteria {
  id = uuidv4();
  type: CriteriaType = 'Operand';
}

export interface WorkProductProps {
  query: Query;
  onCriteriaChanged?: (query: Query) => void;
  userRoleId: number;
}

type WorkProductFieldType =
  | 'SECURITYGROUPS'
  | 'TAGS'
  | 'FOLDERS'
  | 'ASSIGNMENTS'
  | 'REDACTIONS'
  | 'SAVEDSEARCHES'
  | 'PRODUCTIONS'
  | 'SCORES';

const WorkProduct: React.FC<WorkProductProps> = ({
  query,
  onCriteriaChanged,
  userRoleId,
}) => {
  const tempCriteriaRef = useRef<Criteria>(new TemporaryCriteria());
  const [activeDrawer, setActiveDrawer] = useState<WorkProductFieldType | null>(
    null
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [insertEnabled, setInsertEnabled] = useState(false);

  const availableButtonsByRole: Record<string, string[]> = {
    [RoleId.ProjectAdministrator]: [
      'securityGroups',
      'tags',
      'folders',
      'redactionLabels',
      'savedSearchResults',
      'productions',
      'scores',
    ],
    [RoleId.ReviewManager]: [
      'tags',
      'folders',
      'assignments',
      'redactionLabels',
      'savedSearchResults',
      'productions',
      'scores',
    ],
    [RoleId.Reviewer]: [
      'tags',
      'folders',
      'redactionLabels',
      'savedSearchResults',
      'scores',
    ],
  };

  const allowedButtons = availableButtonsByRole[userRoleId] || [];

  const handleCriteriaChanged = (updatedCriteria: Criteria[]) => {
    onCriteriaChanged &&
      onCriteriaChanged({...query, value: [...updatedCriteria]});
  };

  const handleAssignmentCriteriaChange = (value: string) => {
    tempCriteriaRef.current.value = value;
  };

  const handleProductionsCriteriaChange = (
    productions: Field[],
    status: string,
    field: Field | null,
    operator: string | null,
    value: string[]
  ) => {
    const stringBuilder: string[] = [];

    // Productions
    if (productions && productions.length > 0) {
      const names = productions.map((value) => `"${value.text}"`).join(',');
      stringBuilder.push(`name: [${names}]`);
    }

    // Status
    stringBuilder.push(`${status}: "true"`);

    // Field
    if (field) {
      if (operator === 'range') {
        // Multiple: RANGE
        const rangeBuilder: string[] = [];

        const from = value[0];
        if (from) {
          rangeBuilder.push(`"from" : "${from}"`);
        }

        const to = value[1];
        if (to) {
          rangeBuilder.push(`"to" : "${to}"`);
        }

        stringBuilder.push(`${field.text}: [${rangeBuilder.join(', ')}]`);
      } else {
        // Single: IS, LIST
        stringBuilder.push(`${field.text}: "${value[0]}"`);
      }
    }

    tempCriteriaRef.current.value = `productions: [ ${stringBuilder.join(', ')} ]`;
  };

  const handleTagsCriteriaChange = (value: string[]) => {
    const formattedValue = `tags: [
      name: [${value.map((s) => `"${s}"`).join(', ')}]
    ]`;
    tempCriteriaRef.current.value = formattedValue;
  };

  const handleFoldersCriteriaChange = (value: string[]) => {
    const formattedValue = `folders: [
      name: [${value.map((s) => `"${s}"`).join(', ')}]
    ]`;
    tempCriteriaRef.current.value = formattedValue;
  };

  const handleSecurityGroupsCriteriaChange = (value: string[]) => {
    const formattedValue = `securitygroups: [ name: [${value
      .map((s) => `"${s}"`)
      .join(', ')}] ]`;
    tempCriteriaRef.current.value = formattedValue;
  };

  const handleRedactionsCriteriaChange = (value: string[]) => {
    const formattedValue = `redactions: [
      name: [${value.map((s) => `"${s}"`).join(', ')}]
    ]`;
    tempCriteriaRef.current.value = formattedValue;
  };

  const handleSavedSearchesCriteriaChange = (value: string[]) => {
    const formattedValue = `savedSearches: [
      ID: [${value.map((s) => `${s}`).join(', ')}]
    ]`;
    tempCriteriaRef.current.value = formattedValue;
  };

  const handleScoresCriteriaChange = (
    scores: string[],
    min: number | undefined,
    max: number | undefined
  ) => {
    const formattedValue = `scores: [
      ID: [${scores.map((s) => `${s}`).join(', ')}],
      scored: ["min" : "${min ?? 70}", "max" : "${max ?? '*'}"]
    ]`;
    tempCriteriaRef.current.value = formattedValue;
  };

  const handleOperatorSelected = (id: string) => {
    const newCriteria: Criteria = {
      id: uuidv4(),
      type: 'Operator',
      value: getOperatorLabel(id),
    };

    const updatedCriteria = [...query.value, newCriteria];
    onCriteriaChanged && onCriteriaChanged({...query, value: updatedCriteria});
  };

  const openDrawer = (fieldType: WorkProductFieldType) => {
    tempCriteriaRef.current = new TemporaryCriteria();
    setActiveDrawer(fieldType);
    setIsDrawerOpen(true);
  };

  const handleFieldTypeClick = (fieldType: WorkProductFieldType) => {
    openDrawer(fieldType);
  };

  const handleFieldTypeKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    fieldType: WorkProductFieldType
  ) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      openDrawer(fieldType);
    }
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setInsertEnabled(false);
  };

  const handleDrawerSave = () => {
    const updatedCriteria = [...query.value, {...tempCriteriaRef.current}];
    onCriteriaChanged?.({...query, value: updatedCriteria});
    setIsDrawerOpen(false);
    setInsertEnabled(false);
  };

  const drawerConfigs = {
    ['TAGS']: {
      title: t('sessionSearch.queryList.workProduct.tags'),
      component: (
        <TagsFieldType
          type="TAGS"
          onCriteriaChaged={handleTagsCriteriaChange}
          onValidated={setInsertEnabled}
        />
      ),
    },
    ['FOLDERS']: {
      title: t('sessionSearch.queryList.workProduct.folders'),
      component: (
        <FolderFieldType
          type="FOLDERS"
          onCriteriaChaged={handleFoldersCriteriaChange}
          onValidated={setInsertEnabled}
        />
      ),
    },
    ['REDACTIONS']: {
      title: t('sessionSearch.queryList.workProduct.redactionLabels'),
      component: (
        <RedactionLabelsField
          type="REDACTIONS"
          onCriteriaChaged={handleRedactionsCriteriaChange}
          onValidated={setInsertEnabled}
        />
      ),
    },
    ['SAVEDSEARCHES']: {
      title: t('sessionSearch.queryList.workProduct.savedSearchResults'),
      component: (
        <SavedSearchResultsFieldType
          type="SAVEDSEARCHES"
          onCriteriaChaged={handleSavedSearchesCriteriaChange}
          onValidated={setInsertEnabled}
        />
      ),
    },
    ['SECURITYGROUPS']: {
      title: t('sessionSearch.queryList.workProduct.securityGroups'),
      component: (
        <SecurityGroupType
          type="SECURITYGROUPS"
          onCriteriaChaged={handleSecurityGroupsCriteriaChange}
          onValidated={setInsertEnabled}
        />
      ),
    },
    ['SCORES']: {
      title: t('sessionSearch.queryList.workProduct.scores'),
      component: (
        <ScoreFieldType
          type="SCORES"
          onCriteriaChaged={handleScoresCriteriaChange}
          onValidated={setInsertEnabled}
        />
      ),
    },
    ['PRODUCTIONS']: {
      title: t('sessionSearch.queryList.workProduct.productions'),
      component: (
        <ProductionsFieldType
          type="PRODUCTIONS"
          onCriteriaChaged={handleProductionsCriteriaChange}
          onValidated={setInsertEnabled}
        />
      ),
    },
    ['ASSIGNMENTS']: {
      title: t('sessionSearch.queryList.workProduct.assignments'),
      component: (
        <AssignmentFieldType
          type={'ASSIGNMENTS'}
          onCriteriaChaged={handleAssignmentCriteriaChange}
          onValidated={setInsertEnabled}
        />
      ),
    },
  };

  return (
    <>
      <Stack spacing={1} className="work-product-content-panel">
        <Stack direction="row" className="query-actions" flexWrap="wrap">
          <Typography
            className="typo-text label3_size query-actions-label"
            data-testid={`work-product-insert`}
          >
            {t('sessionSearch.queryList.workProduct.insert')}
          </Typography>

          {allowedButtons.includes('securityGroups') && (
            <ArrButton
              size="small"
              variant="secondary"
              onClick={() => handleFieldTypeClick('SECURITYGROUPS')}
              onKeyDown={(e) => handleFieldTypeKeyDown(e, 'SECURITYGROUPS')}
              className="button-wp-add-security-groups arrbutton-nowrap"
              dataTestId="button-wp-add-security-groups"
            >
              {t('sessionSearch.queryList.workProduct.securityGroups')}
            </ArrButton>
          )}

          {allowedButtons.includes('tags') && (
            <ArrButton
              size="small"
              variant="secondary"
              onClick={() => handleFieldTypeClick('TAGS')}
              onKeyDown={(e) => handleFieldTypeKeyDown(e, 'TAGS')}
              className="button-wp-add-tags arrbutton-nowrap"
              dataTestId="button-wp-add-tags"
            >
              {t('sessionSearch.queryList.workProduct.tags')}
            </ArrButton>
          )}

          {allowedButtons.includes('folders') && (
            <ArrButton
              size="small"
              variant="secondary"
              onClick={() => handleFieldTypeClick('FOLDERS')}
              onKeyDown={(e) => handleFieldTypeKeyDown(e, 'FOLDERS')}
              className="button-wp-add-folders arrbutton-nowrap"
              dataTestId="button-wp-add-folders"
            >
              {t('sessionSearch.queryList.workProduct.folders')}
            </ArrButton>
          )}

          {allowedButtons.includes('assignments') && (
            <ArrButton
              size="small"
              variant="secondary"
              onClick={() => handleFieldTypeClick('ASSIGNMENTS')}
              onKeyDown={(e) => handleFieldTypeKeyDown(e, 'ASSIGNMENTS')}
              className="button-wp-add-assignments arrbutton-nowrap"
              dataTestId="button-wp-add-assignments"
            >
              {t('sessionSearch.queryList.workProduct.assignments')}
            </ArrButton>
          )}

          {allowedButtons.includes('redactionLabels') && (
            <ArrButton
              size="small"
              variant="secondary"
              onClick={() => handleFieldTypeClick('REDACTIONS')}
              onKeyDown={(e) => handleFieldTypeKeyDown(e, 'REDACTIONS')}
              className="button-wp-add-redaction-labels arrbutton-nowrap"
              dataTestId="button-wp-add-redaction-labels"
            >
              {t('sessionSearch.queryList.workProduct.redactionLabels')}
            </ArrButton>
          )}

          {allowedButtons.includes('savedSearchResults') && (
            <ArrButton
              size="small"
              variant="secondary"
              onClick={() => handleFieldTypeClick('SAVEDSEARCHES')}
              onKeyDown={(e) => handleFieldTypeKeyDown(e, 'SAVEDSEARCHES')}
              className="button-wp-add-saved-search-results arrbutton-nowrap"
              dataTestId="button-wp-add-saved-search-results"
            >
              {t('sessionSearch.queryList.workProduct.savedSearchResults')}
            </ArrButton>
          )}

          {allowedButtons.includes('productions') && (
            <ArrButton
              size="small"
              variant="secondary"
              onClick={() => handleFieldTypeClick('PRODUCTIONS')}
              onKeyDown={(e) => handleFieldTypeKeyDown(e, 'PRODUCTIONS')}
              className="button-wp-add-productions arrbutton-nowrap"
              dataTestId="button-wp-add-productions"
            >
              {t('sessionSearch.queryList.workProduct.productions')}
            </ArrButton>
          )}

          {allowedButtons.includes('scores') && (
            <ArrButton
              size="small"
              variant="secondary"
              onClick={() => handleFieldTypeClick('SCORES')}
              onKeyDown={(e) => handleFieldTypeKeyDown(e, 'SCORES')}
              className="button-wp-add-scores arrbutton-nowrap"
              dataTestId="button-wp-add-scores"
            >
              {t('sessionSearch.queryList.workProduct.scores')}
            </ArrButton>
          )}

          <ArrOperatorButton
            onOperatorSelected={handleOperatorSelected}
            dataTestId="work-product"
          />
        </Stack>

        <Box
          className="query-chips"
          data-testid="chip-panel-work-product-criteria-wrapper"
        >
          <ChipPanel
            criteria={query.value}
            onCriteriaChange={handleCriteriaChanged}
            dataTestId="work-product-panel"
          />
        </Box>
      </Stack>
      {isDrawerOpen && activeDrawer && (
        <FieldTypeDrawer
          title={drawerConfigs[activeDrawer].title}
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          onSave={handleDrawerSave}
          canSave={insertEnabled}
        >
          {drawerConfigs[activeDrawer].component}
        </FieldTypeDrawer>
      )}
    </>
  );
};
export default WorkProduct;
